import { CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  effect,
  inject,
  input,
  signal,
  Type,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';

import { switchMap, combineLatest, map, distinctUntilChanged } from 'rxjs';
import { startWith } from 'rxjs/operators';

import type { DefineFormData, DynamicFormConfig } from '@core/ask-hr-dynamic-forms-types';
import { FnCallPipe, RtComponentOutlet } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { IconComponent, ProtectedControlDirective } from '@core/user-interface-legacy';
import { deepEqual, generateUuid } from '@core/utils-legacy';

import { DynamicFormConfigService } from '../../dynamic-form-config.service';

export type DefineFormState<T extends DynamicFormConfig> = T extends DynamicFormConfig
  ? {
      kind: T['kind'];
      hash: string;
      value: ReturnType<ReturnType<T['defineForm']>['getRawValue']>;
    }
  : never;

interface DefineFormComponentConfig {
  uuid: string;
  kind: string;
  component: Type<any>;
  inputs: {
    defineConfig: object;
    defineForm: AbstractControl<unknown>;
  };
}

@Component({
  selector: 'dynamic-form-define',
  template: `
    <div
      class="dynamic-form-list"
      cdkDropList
      (cdkDropListDropped)="moveComponent($event)"
      margin-bottom-medium
    >
      @for (cmp of formComponents(); track cmp.uuid) {
        <ng-template
          [rtProtectedControl]="cmp.inputs.defineForm"
          [rtComponentOutlet]="cmp.component"
          [rtComponentOutletInputs]="cmp.inputs"
          [rtComponentOutletOutputs]="{
            delete: wrapFn | fnCall: deleteComponent : cmp.uuid,
            duplicate: wrapFn | fnCall: duplicateComponent : cmp.uuid,
            changeTemplate: wrapFn | fnCall: changeTemplate : cmp.uuid,
          }"
        />
      }
    </div>
    <div class="rt-card" padding>
      <p text-weight-semi-bold text-small-x>
        {{ 'tlc.dynamic_form.define.add_question' | translate }}
      </p>
      <div class="question-selector">
        @for (config of selectableConfigs; track config.value) {
          <div class="rt-card rt-card-clickable add-question" (click)="addComponent(config.value)">
            <rt-icon [name]="config.icon!" [colorToken]="'colorIconPrimaryDefault'" />
            <span text-semi-medium text-darkest>
              {{ config.option }}
            </span>
          </div>
        }
      </div>
    </div>
  `,
  imports: [
    IconComponent,
    RtComponentOutlet,
    FnCallPipe,
    CdkDropList,
    ProtectedControlDirective,
    TranslatePipe,
  ],
  providers: [DynamicFormConfigService],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dynamic-form-define.component.less',
})
export class DynamicFormDefineComponent {
  private readonly dynamicFormConfigService = inject(DynamicFormConfigService);
  protected readonly formComponents = signal<DefineFormComponentConfig[]>([]);

  protected readonly formState$ = toObservable(this.formComponents).pipe(
    switchMap(components =>
      combineLatest(
        components.map(component =>
          component.inputs.defineForm.valueChanges.pipe(
            startWith(component.inputs.defineForm.value),
            map(
              value =>
                <DefineFormState<DynamicFormConfig>>{
                  kind: component.kind,
                  hash: component.uuid,
                  value,
                },
            ),
          ),
        ),
      ),
    ),
    distinctUntilChanged((a, b) => deepEqual(a, b)),
  );

  protected readonly formStatus$ = toObservable(this.formComponents).pipe(
    switchMap(components =>
      combineLatest(
        components.map(component => {
          const { statusChanges, status } = component.inputs.defineForm;
          return statusChanges.pipe(startWith(status));
        }),
      ),
    ),
  );

  public formState = input.required<Array<DefineFormState<DynamicFormConfig>>>();
  public formStateChanged = outputFromObservable(this.formState$);
  public formStatusChanged = outputFromObservable(this.formStatus$);

  protected selectableConfigs = this.dynamicFormConfigService.defineAvailableConfigs;

  private initForm = effect(() => {
    const initialFormState = this.formState();
    untracked(() => this.initializeForm(initialFormState));
  });

  public getFromComponents = () => this.formComponents();

  protected wrapFn =
    (fn: (...args: any[]) => void, ...args: any[]) =>
    <T>(arg: T) =>
      fn.apply(this, [...args, arg]);

  protected async changeTemplate(uuid: string, kind: string) {
    const components = this.formComponents();
    const toReplace = components.find(component => component.uuid === uuid);
    if (!toReplace) return;

    const formData = toReplace.inputs.defineForm.getRawValue();
    const newComponent = await this.createFormComponent(kind, toReplace.uuid, {
      kind: toReplace.kind,
      value: formData,
    });
    this.replaceComponent(toReplace.uuid, newComponent);
  }

  protected async addComponent(kind: string, formData?: DefineFormData) {
    const formComponent = await this.createFormComponent(kind, undefined, formData);
    this.formComponents.update(v => [...v, formComponent]);
    return formComponent;
  }

  protected deleteComponent(uuid: string) {
    this.formComponents.update(components =>
      components.filter(component => component.uuid !== uuid),
    );
  }

  protected replaceComponent(uuid: string, config: DefineFormComponentConfig) {
    this.formComponents.update(componentConfigs =>
      componentConfigs.map(componentConfig =>
        componentConfig.uuid === uuid ? config : componentConfig,
      ),
    );
  }

  protected clearComponents() {
    this.formComponents.set([]);
  }

  protected duplicateComponent(uuid: string) {
    const componentToDuplicate = this.formComponents().find(component => component.uuid === uuid);
    if (!componentToDuplicate) return;

    const { kind } = componentToDuplicate;
    const formValue = componentToDuplicate.inputs.defineForm.getRawValue();
    this.addComponent(kind, { kind, value: formValue });
  }

  protected moveComponent(dropEvent: { previousIndex: number; currentIndex: number }) {
    const components = [...this.formComponents()];
    moveItemInArray(components, dropEvent.previousIndex, dropEvent.currentIndex);
    this.formComponents.set(components);
  }

  private async initializeForm(configs: Array<DefineFormState<DynamicFormConfig>>) {
    this.clearComponents();
    const formComponents = await Promise.all(
      configs.map(config =>
        this.createFormComponent(config.kind, config.hash, {
          kind: config.kind,
          value: config.value,
        }),
      ),
    );
    this.formComponents.set(formComponents);
  }

  private async createFormComponent(
    kind: string,
    hash?: string,
    formData?: DefineFormData,
  ): Promise<DefineFormComponentConfig> {
    const config = this.dynamicFormConfigService.configsMap[kind];
    const componentType = await config.components.define();
    return {
      uuid: hash ?? generateUuid(),
      kind: config.kind,
      component: componentType,
      inputs: {
        defineConfig: config.defineConfig,
        defineForm: config.defineForm(formData),
      },
    };
  }
}
